/**
 * SelCyclePopup — the selection-cycling candidate list (Faz 4.4).
 *
 * Rendered at the DOM layer (Mode2D shell) while the selection router
 * reports a stacked pick with SELECTIONCYCLING on: one row per pierced
 * entity (type / layer / color), clicking a row selects that entity —
 * the classic overlapping-objects pick list. Closes on outside pointer
 * down, Escape or after a row pick.
 */

import { useEffect } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelCycleStore } from '../../state/useSelCycleStore';
import { useAppStore } from '../../state/store';

export function SelCyclePopup() {
    const { t } = useTranslation();
    const visible = useSelCycleStore((s) => s.visible);
    const position = useSelCycleStore((s) => s.position);
    const candidates = useSelCycleStore((s) => s.candidates);

    useEffect(() => {
        if (!visible) return;
        const onDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest?.('[data-selcycle-popup]')) return;
            useSelCycleStore.getState().close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') useSelCycleStore.getState().close();
        };
        const timer = window.setTimeout(() => {
            document.addEventListener('pointerdown', onDown, true);
            document.addEventListener('keydown', onKey);
        }, 0);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [visible]);

    if (!visible || candidates.length === 0) return null;

    const pick = (id: string) => {
        useAppStore.getState().select([id]);
        useSelCycleStore.getState().close();
    };

    const clampedX = Math.min(position.x, (typeof window !== 'undefined' ? window.innerWidth : 800) - 210);
    const clampedY = Math.min(position.y, (typeof window !== 'undefined' ? window.innerHeight : 600) - 40 * Math.min(candidates.length, 6) - 16);

    return (
        <Box
            data-selcycle-popup
            position="fixed"
            left={Math.max(8, clampedX)}
            top={Math.max(8, clampedY)}
            minW="180px"
            bg="bg.panel"
            border="1px solid"
            borderColor="border.base"
            borderRadius="sm"
            boxShadow="md"
            py={1}
            zIndex={30}
        >
            <Box px={3} py={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                {t('statusbar.selCycleTitle')}
            </Box>
            {candidates.map((c, i) => (
                <Flex
                    key={c.id}
                    as="button"
                    w="100%"
                    px={3}
                    py={1}
                    align="center"
                    gap={2}
                    fontSize="xs"
                    color="text.primary"
                    cursor="pointer"
                    _hover={{ bg: 'bg.active' }}
                    onClick={() => pick(c.id)}
                    textAlign="left"
                >
                    <Box
                        w="10px"
                        h="10px"
                        borderRadius="sm"
                        flexShrink={0}
                        border="1px solid"
                        borderColor="border.base"
                        // Runtime swatch of the entity's own color value — a
                        // computed value, not a static style.
                        bg={c.color ?? 'transparent'}
                    />
                    <Text isTruncated flex="1">
                        {i + 1}. {c.type}
                    </Text>
                    <Text isTruncated maxW="40%" color="text.secondary" fontSize="10px">
                        {c.layerName}
                    </Text>
                </Flex>
            ))}
        </Box>
    );
}
